import { definitionFor, definitionVersionText, metricKeyFor } from '../piDefinitions.js';
import { ACTIVATION_GLOSSARY } from './activation.js';
import { AREAS_GLOSSARY } from './areas.js';
import { ASSISTANT_GLOSSARY } from './assistant.js';
import { COMMON_GLOSSARY, STATE_INFO_PREFIX, STATE_LEGEND_INFO_KEY, STATE_LEGEND_LABEL, stateInfoKey } from './common.js';
import { COMPANIES_GLOSSARY } from './companies.js';
import { ENGAGEMENT_GLOSSARY } from './engagement.js';
import { FEATURES_GLOSSARY } from './features.js';
import { FILTERS_GLOSSARY, FILTER_INFO_ALIASES, FILTER_INFO_PREFIX, filterInfoKey } from './filters.js';
import { OPERATIONS_GLOSSARY } from './operations.js';
import { OVERVIEW_GLOSSARY } from './overview.js';
import { RETENTION_GLOSSARY } from './retention.js';
import { REVENUE_GLOSSARY } from './revenue.js';
import { STATUS_GLOSSARY } from './status.js';

export { FILTER_INFO_ALIASES, FILTER_INFO_PREFIX, STATE_INFO_PREFIX, STATE_LEGEND_INFO_KEY, STATE_LEGEND_LABEL, filterInfoKey, stateInfoKey };

export const GLOSSARY_BLOCK_LIMIT = 280;

export const GLOSSARY_BLOCKS = [
  { field: 'whatIs', label: 'O que é' },
  { field: 'purpose', label: 'Para que serve' },
  { field: 'howGenerated', label: 'Como é gerado' },
];

export const GLOSSARY_FIELDS = ['key', 'label', 'whatIs', 'purpose', 'howGenerated', 'caveat', 'metricKey', 'seeAlso'];

export const CAVEAT_LABEL = 'Cuidados de leitura';

export const SEE_ALSO_LABEL = 'Veja também';

export const GLOSSARY_FILES = [
  { file: 'common.js', prefix: 'common.', entries: COMMON_GLOSSARY },
  { file: 'filters.js', prefix: FILTER_INFO_PREFIX, entries: FILTERS_GLOSSARY },
  { file: 'overview.js', prefix: 'overview.', entries: OVERVIEW_GLOSSARY },
  { file: 'engagement.js', prefix: 'engagement.', entries: ENGAGEMENT_GLOSSARY },
  { file: 'features.js', prefix: 'features.', entries: FEATURES_GLOSSARY },
  { file: 'companies.js', prefix: 'companies.', entries: COMPANIES_GLOSSARY },
  { file: 'activation.js', prefix: 'activation.', entries: ACTIVATION_GLOSSARY },
  { file: 'retention.js', prefix: 'retention.', entries: RETENTION_GLOSSARY },
  { file: 'operations.js', prefix: 'operations.', entries: OPERATIONS_GLOSSARY },
  { file: 'revenue.js', prefix: 'revenue.', entries: REVENUE_GLOSSARY },
  { file: 'assistant.js', prefix: 'assistant.', entries: ASSISTANT_GLOSSARY },
  { file: 'status.js', prefix: 'status.', entries: STATUS_GLOSSARY },
  { file: 'areas.js', prefix: 'areas.', entries: AREAS_GLOSSARY },
];

export const GLOSSARY = GLOSSARY_FILES.reduce((all, group) => all.concat(Array.isArray(group.entries) ? group.entries : []), []);

const KEY_PATTERN = /^[a-z][A-Za-z0-9]*(?:\.[A-Za-z0-9_]+)+$/;

const byKey = {};

GLOSSARY.forEach((entry) => {
  if (entry && typeof entry.key === 'string' && !Object.prototype.hasOwnProperty.call(byKey, entry.key)) byKey[entry.key] = entry;
});

const textOf = (value) => (typeof value === 'string' ? value.trim() : '');

export const glossaryEntry = (key) => (typeof key === 'string' && Object.prototype.hasOwnProperty.call(byKey, key) ? byKey[key] : null);

export const entryProblems = (entry, prefix) => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return ['entrada que não é objeto'];
  const name = typeof entry.key === 'string' && entry.key ? entry.key : '(sem chave)';
  const problems = [];
  const add = (text) => problems.push(name + ': ' + text);
  if (!KEY_PATTERN.test(textOf(entry.key))) add('chave fora do padrão dominio.nome');
  if (prefix && textOf(entry.key).indexOf(prefix) !== 0) add('chave deveria começar com ' + prefix);
  if (!textOf(entry.label)) add('sem rótulo');
  GLOSSARY_BLOCKS.forEach((block) => {
    const text = textOf(entry[block.field]);
    if (!text) add('bloco "' + block.label + '" vazio');
    else if (text.length > GLOSSARY_BLOCK_LIMIT) add('bloco "' + block.label + '" com ' + text.length + ' caracteres (máximo ' + GLOSSARY_BLOCK_LIMIT + ')');
  });
  if (entry.caveat !== undefined) {
    const caveat = textOf(entry.caveat);
    if (!caveat) add('cuidado de leitura vazio');
    else if (caveat.length > GLOSSARY_BLOCK_LIMIT) add('cuidado de leitura com ' + caveat.length + ' caracteres (máximo ' + GLOSSARY_BLOCK_LIMIT + ')');
  }
  if (entry.metricKey !== undefined && !metricKeyFor(entry.metricKey)) add('metricKey sem definição em piDefinitions.js: ' + entry.metricKey);
  if (entry.seeAlso !== undefined) {
    if (!Array.isArray(entry.seeAlso) || !entry.seeAlso.length) add('seeAlso deve ser uma lista de chaves');
    else {
      entry.seeAlso.forEach((key) => {
        if (key === entry.key) add('seeAlso aponta para a própria entrada');
        else if (!glossaryEntry(key)) add('seeAlso sem entrada no glossário: ' + key);
      });
    }
  }
  Object.keys(entry).forEach((field) => {
    if (GLOSSARY_FIELDS.indexOf(field) === -1) add('campo desconhecido: ' + field);
  });
  return problems;
};

export const glossaryProblems = () => {
  const problems = [];
  const seen = {};
  GLOSSARY_FILES.forEach((group) => {
    if (!Array.isArray(group.entries)) {
      problems.push(group.file + ': não exporta uma lista');
      return;
    }
    group.entries.forEach((entry) => {
      entryProblems(entry, group.prefix).forEach((problem) => problems.push(group.file + ': ' + problem));
      const key = entry && typeof entry.key === 'string' ? entry.key : '';
      if (key && seen[key]) problems.push(group.file + ': chave repetida ' + key + ' (já em ' + seen[key] + ')');
      if (key && !seen[key]) seen[key] = group.file;
    });
  });
  return problems;
};

const seeAlsoLabels = (entry) => (Array.isArray(entry.seeAlso) ? entry.seeAlso : [])
  .map((key) => glossaryEntry(key))
  .filter((found) => !!found)
  .map((found) => textOf(found.label));

export const glossaryPanel = (entry, options) => {
  if (!entry || typeof entry !== 'object') return null;
  const settings = options || {};
  const definition = entry.metricKey ? definitionFor(entry.metricKey, settings.overrides) : null;
  const formula = definition && typeof definition.formula === 'string' ? definition.formula : textOf(entry.formula);
  let versionText = '';
  if (entry.metricKey) versionText = definitionVersionText(definition, settings.version);
  else if (typeof entry.version === 'number' && entry.version > 0) versionText = 'Versão ' + entry.version;
  return {
    title: textOf(entry.label) || textOf(settings.label) || 'indicador',
    blocks: GLOSSARY_BLOCKS
      .map((block) => ({ label: block.label, text: textOf(entry[block.field]) }))
      .filter((block) => !!block.text),
    formula: formula,
    caveat: textOf(entry.caveat),
    seeAlso: seeAlsoLabels(entry),
    versionText: versionText,
  };
};

export const glossaryText = (entry, options) => {
  const panel = glossaryPanel(entry, options);
  if (!panel) return '';
  const parts = panel.blocks.map((block) => block.label + ': ' + block.text);
  if (panel.formula) parts.push('Fórmula: ' + panel.formula + (/[.!?]$/.test(panel.formula) ? '' : '.'));
  if (panel.caveat) parts.push(CAVEAT_LABEL + ': ' + panel.caveat);
  if (panel.versionText) parts.push(panel.versionText + '.');
  return parts.join(' ');
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/glossary/index.js